"use client";

import { useCallback, useState } from "react";
import { Track } from "@/src/lib/tracking";
import { mergeAdjustAdidIntoHeaders } from "@/src/lib/bridge/adjustRequestHeaders";
import { getMemberApi } from "@/src/services/helper";
import type {
  PayChannel,
  PaymentConfirmRequest,
} from "@features/api/memberApi";
import {
  saveCheckoutStash,
  takeCheckoutStash,
} from "@utils/member/checkoutSessionStash";
import Translations from "@translate/hooks/useCheckoutReturnConfirm";
import { I18nUtil } from "@utils/i18n";
import { sleep as defaultSleep } from "@utils/sleep";

const I18n = I18nUtil.createScoped(Translations);

/**
 * 托管收银台回跳后的主动确认（EDG-552，fe-spec §3.4 ①、§4.6 F4）。
 *
 * 回跳 ≠ 生效：用跳转前暂存的 session_id 调 `/payment/confirm`，false 时短轮询。
 * 8 × 1.5s ≈ 12 秒（BG-2 目标 P95 ≤ 5 秒）；超时只提示「生效中」由 webhook 兜底，**从不报支付失败**。
 */
export const CONFIRM_POLL_INTERVAL_MS = 1500;
export const CONFIRM_MAX_ATTEMPTS = 8;

export type ConfirmResult = "confirmed" | "pending_timeout" | "no_session";

export interface ConfirmOutcome {
  result: ConfirmResult;
  attempts: number;
  elapsedMs: number;
}

export interface ConfirmCheckoutParams {
  planId: string;
  channel: PayChannel;
  /** 注入以便单测；默认 memberApi.payment.paymentConfirm */
  confirm?: (req: PaymentConfirmRequest) => Promise<boolean>;
  /** 注入以便单测；默认 setTimeout */
  sleep?: (ms: number) => Promise<void>;
  /** 组件卸载时中止后续请求 */
  signal?: AbortSignal;
}

const LOG_TAG = "Member";

/** 确认超时的唯一提示（定价页 / onboarding 回跳共用）：只说「生效中」，从不说失败（AC-15）。 */
export const buildConfirmPendingMessage = (): string =>
  I18n.t("Payment received. Your membership will be activated shortly.");

async function defaultConfirm(req: PaymentConfirmRequest): Promise<boolean> {
  const api = await getMemberApi();
  const res = await api.payment.paymentConfirm(req, {
    headers: mergeAdjustAdidIntoHeaders({}),
  });
  return res.data?.data?.result === true;
}

/** 网络错误 / 5xx 计为一次未完成，不抛（轮询继续，永不据此展示失败） */
async function confirmOnce(
  confirm: NonNullable<ConfirmCheckoutParams["confirm"]>,
  req: PaymentConfirmRequest,
): Promise<boolean> {
  try {
    return await confirm(req);
  } catch (error) {
    Track.log("warn", LOG_TAG, "[trace][pay] confirm attempt failed", {
      cause: String(error),
    });
    return false;
  }
}

function reportOutcome(
  params: ConfirmCheckoutParams,
  outcome: ConfirmOutcome,
): ConfirmOutcome {
  Track.event("checkout_result", {
    channel: params.channel,
    product_id: params.planId,
    result: outcome.result,
    attempts: outcome.attempts,
    elapsed_ms: outcome.elapsedMs,
  });
  Track.log("info", LOG_TAG, "[trace][pay] confirm", {
    result: outcome.result,
    attempts: String(outcome.attempts),
  });
  return outcome;
}

/**
 * 流程函数：取暂存 → 循环 confirm；结束时上报 `checkout_result`。
 *
 * 暂存读后即删只防「已确认的会话被重复确认」；没确认成功（超时 / 中止）时放回暂存，
 * 让用户之后点「Check payment status」还能用同一 session_id 再确认（Q6）。暂存自身 1 小时过期。
 */
export async function confirmCheckoutWithPolling(
  params: ConfirmCheckoutParams,
): Promise<ConfirmOutcome> {
  const {
    planId,
    channel,
    confirm = defaultConfirm,
    sleep = defaultSleep,
    signal,
  } = params;
  const startedAt = Date.now();
  const stash = takeCheckoutStash("checkout", planId);
  if (!stash) {
    return reportOutcome(params, {
      result: "no_session",
      attempts: 0,
      elapsedMs: 0,
    });
  }
  const req: PaymentConfirmRequest = {
    purchase_token: stash.sessionId,
    pay_channel: channel,
    product_id: planId,
  };
  let attempts = 0;
  while (attempts < CONFIRM_MAX_ATTEMPTS && !signal?.aborted) {
    attempts += 1;
    if (await confirmOnce(confirm, req)) {
      return reportOutcome(params, {
        result: "confirmed",
        attempts,
        elapsedMs: Date.now() - startedAt,
      });
    }
    if (attempts < CONFIRM_MAX_ATTEMPTS && !signal?.aborted) {
      await sleep(CONFIRM_POLL_INTERVAL_MS);
    }
  }
  saveCheckoutStash(stash);
  const outcome: ConfirmOutcome = {
    result: "pending_timeout",
    attempts,
    elapsedMs: Date.now() - startedAt,
  };
  // 中止（页面卸载）不是确认超时，不上报，免得把离开页面算进 BG-2 的超时率
  return signal?.aborted ? outcome : reportOutcome(params, outcome);
}

/**
 * 用户手动「Check payment status」时补确认一次（Q6）：有暂存就调一次 `/payment/confirm`。
 * 不上报 `checkout_result`（那是回跳自动确认的口径，手动重试不计入）；没确认成功时放回暂存，可再次重试。
 *
 * @returns 是否确认已生效；没有暂存（已过期 / 已确认过 / 无痕模式）→ false
 */
export async function confirmCheckoutOnce(
  params: Pick<ConfirmCheckoutParams, "planId" | "channel" | "confirm">,
): Promise<boolean> {
  const { planId, channel, confirm = defaultConfirm } = params;
  const stash = takeCheckoutStash("checkout", planId);
  if (!stash) {
    return false;
  }
  const confirmed = await confirmOnce(confirm, {
    purchase_token: stash.sessionId,
    pay_channel: channel,
    product_id: planId,
  });
  if (!confirmed) {
    saveCheckoutStash(stash);
  }
  Track.log("info", LOG_TAG, "[trace][pay] manual confirm", {
    result: String(confirmed),
  });
  return confirmed;
}

/** 组件侧：暴露 confirming 状态给确认遮罩（F4）。 */
export function useCheckoutReturnConfirm(): {
  isConfirming: boolean;
  run: (
    params: Pick<ConfirmCheckoutParams, "planId" | "channel" | "signal">,
  ) => Promise<ConfirmOutcome>;
} {
  const [isConfirming, setIsConfirming] = useState(false);
  const run = useCallback(
    async (
      params: Pick<ConfirmCheckoutParams, "planId" | "channel" | "signal">,
    ): Promise<ConfirmOutcome> => {
      setIsConfirming(true);
      try {
        return await confirmCheckoutWithPolling(params);
      } finally {
        setIsConfirming(false);
      }
    },
    [],
  );
  return { isConfirming, run };
}
